import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Fingerprint, ShieldCheck, ShieldAlert, KeyRound, Loader2, ArrowLeft, Search, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";

const Field = ({ label, value, accent }) => (
  <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/5 last:border-0">
    <span className="font-mono-sig text-[10px] tracking-[0.25em] uppercase text-[#52525B]">{label}</span>
    <span className={`font-mono-sig text-sm ${accent || "text-white"}`}>{value ?? "—"}</span>
  </div>
);

const printCertificate = (r, pk, qrDataUrl) => {
  const now = new Date();
  const esc = (s) => String(s == null ? "—" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const row = (k, v) => `<tr><td class="k">${k}</td><td class="v">${esc(v)}</td></tr>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>SIGNET Certificate of Authenticity</title>
  <style>
    *{box-sizing:border-box} body{font-family:'Courier New',monospace;background:#0A0A0C;color:#0A0A0C;margin:0;padding:32px;}
    .cert{max-width:800px;margin:0 auto;background:#fff;border:3px solid #0A0A0C;border-radius:16px;padding:40px 44px;position:relative;}
    .band{height:8px;background:linear-gradient(90deg,#00F0FF,#00FF9D,#FFE600);border-radius:8px;margin-bottom:28px;}
    h1{font-size:26px;letter-spacing:1px;margin:0 0 4px;}
    .sub{color:#00838f;font-weight:bold;letter-spacing:3px;text-transform:uppercase;font-size:11px;margin-bottom:24px;}
    .seal{position:absolute;top:34px;right:40px;width:88px;height:88px;border:3px solid #00838f;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#00838f;font-weight:bold;font-size:10px;text-align:center;line-height:1.2;transform:rotate(-12deg);}
    table{width:100%;border-collapse:collapse;margin:8px 0 22px;}
    td{padding:9px 6px;border-bottom:1px solid #e3e3e3;font-size:13px;vertical-align:top;}
    td.k{color:#666;text-transform:uppercase;font-size:10px;letter-spacing:1px;width:170px;}
    td.v{word-break:break-all;font-weight:bold;}
    .verdict{display:inline-block;background:#00FF9D;color:#0A0A0C;font-weight:bold;padding:6px 14px;border-radius:999px;font-size:12px;letter-spacing:1px;}
    .note{font-size:11px;color:#555;line-height:1.5;border-top:1px dashed #ccc;padding-top:14px;margin-top:8px;}
    .foot{display:flex;justify-content:space-between;font-size:10px;color:#888;margin-top:20px;text-transform:uppercase;letter-spacing:1px;}
    @media print{body{padding:0;background:#fff;}}
  </style></head><body>
  <div class="cert">
    <div class="band"></div>
    <div class="seal">SIGNET<br/>VERIFIED<br/>Ed25519</div>
    <h1>Certificate of Authenticity</h1>
    <div class="sub">SIGNET Network · Public-Key Verified</div>
    <p><span class="verdict">✓ AUTHENTIC</span></p>
    ${qrDataUrl ? `<div style="float:right;text-align:center;margin:0 0 10px 18px;">
      <img src="${qrDataUrl}" width="120" height="120" style="border:1px solid #ddd;border-radius:8px;padding:4px;background:#fff;"/>
      <div style="font-size:9px;color:#666;letter-spacing:1px;text-transform:uppercase;margin-top:4px;">Scan to re-verify</div>
    </div>` : ""}
    <table>
      ${row("Cardholder", r.cardholder_name)}
      ${row("Card number", r.card_number || r.card_id)}
      ${row("Public signature", r.signature_public)}
      ${row("Network / Source", (r.network || "") + " · " + (r.source || ""))}
      ${row("Reality", r.reality)}
      ${row("Issued by", r.issued_by)}
      ${row("Algorithm", r.algorithm)}
      ${row("Authenticity signature", r.authenticity)}
      ${row("Network public key", pk ? pk.public_key : "")}
      ${row("Verified at", now.toISOString())}
    </table>
    <p class="note">This certificate attests that the card above was genuinely minted and cryptographically
    signed by the SIGNET network, and that its authenticity signature verifies against SIGNET's published
    Ed25519 public key. It may be presented as proof of a genuine SIGNET card. Anyone can independently
    re-verify at the SIGNET verification page using the card number or signature above.</p>
    <div class="foot"><span>SIGNET · Made by Nothing Guilty</span><span>Certificate ${now.getTime().toString(36).toUpperCase()}</span></div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
  </body></html>`;
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
};

export default function Verify() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [cardId, setCardId] = useState(params.get("id") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [pubKey, setPubKey] = useState(null);

  useEffect(() => {
    api.get("/network/public-key").then((r) => setPubKey(r.data)).catch(() => {});
  }, []);

  const runVerify = async (id) => {
    const target = (id ?? cardId).trim();
    if (!target) {
      setError("Paste a card ID to verify.");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await api.get(`/network/verify/${encodeURIComponent(target)}`);
      setResult(res.data);
      setParams({ id: target });
    } catch (e) {
      setError(e.response?.status === 404 ? "No such card on the SIGNET network." : "Verification failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const initial = params.get("id");
    if (initial) runVerify(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authentic = result?.authentic;

  return (
    <div className="min-h-screen bg-[#0A0A0C] relative overflow-hidden">
      <div className="pointer-events-none fixed top-[-120px] left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-[#00FF9D] opacity-[0.07] blur-[150px]" />
      <div className="pointer-events-none fixed bottom-[-160px] right-[-80px] w-[520px] h-[420px] rounded-full bg-[#00F0FF] opacity-[0.06] blur-[150px]" />

      <header className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <button data-testid="verify-logo" onClick={() => navigate("/")} className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#00F0FF] flex items-center justify-center">
            <Fingerprint className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg tracking-tighter">SIGNET</span>
        </button>
        <button
          data-testid="verify-home-link"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm text-[#A0A0AB] hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <p className="font-mono-sig text-[11px] tracking-[0.3em] uppercase text-[#00FF9D] mb-3">Public authenticity check</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] mb-4">
          Verify a<br />SIGNET card.
        </h1>
        <p className="text-[#A0A0AB] text-base mb-8 max-w-lg">
          Enter a card number, or the crypto signature printed on the card, to confirm it was genuinely
          minted and signed by the SIGNET network. Verification runs against our published Ed25519
          public key — no account, no secret required.
        </p>

        <div className="flex items-center gap-3 mb-2">
          <Input
            data-testid="verify-input"
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runVerify()}
            placeholder="Card number or signature (e.g. Z2PU QW85 WCMZ F2VG)"
            className="bg-white/5 border-white/10 text-white h-12 font-mono-sig text-sm"
          />
          <Button
            data-testid="verify-submit"
            onClick={() => runVerify()}
            disabled={busy}
            className="rounded-full bg-[#00FF9D] text-black hover:bg-[#00FF9D]/90 font-semibold h-12 px-6 shrink-0"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Verify</>}
          </Button>
        </div>
        {error && <p data-testid="verify-error" className="text-sm text-[#FF007F] mb-2">{error}</p>}

        <AnimatePresence mode="wait">
          {result && (
            <motion.div
              key={result.card_id}
              data-testid="verify-result"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`mt-6 rounded-3xl border p-6 md:p-7 backdrop-blur-xl ${authentic ? "border-[#00FF9D]/30 bg-[#00FF9D]/[0.05]" : "border-[#FF007F]/30 bg-[#FF007F]/[0.05]"}`}
            >
              <div className="flex items-center gap-4 mb-5">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${authentic ? "bg-[#00FF9D]/15 border border-[#00FF9D]/40" : "bg-[#FF007F]/15 border border-[#FF007F]/40"}`}>
                  {authentic ? <ShieldCheck className="w-7 h-7 text-[#00FF9D]" /> : <ShieldAlert className="w-7 h-7 text-[#FF007F]" />}
                </div>
                <div>
                  <p data-testid="verify-verdict" className={`font-display font-black text-2xl tracking-tight ${authentic ? "text-[#00FF9D]" : "text-[#FF007F]"}`}>
                    {authentic ? "Authentic SIGNET card" : "Not verified"}
                  </p>
                  <p className="text-sm text-[#A0A0AB]">
                    {authentic
                      ? "This card's signature matches the network public key."
                      : "This card could not be verified against the SIGNET network."}
                  </p>
                </div>
              </div>

              <div className="rounded-2xl bg-black/20 border border-white/5 px-4">
                <Field label="Card number" value={<span className="text-[#00F0FF] break-all">{result.card_number || result.card_id}</span>} />
                <Field label="Signature" value={<span className="text-[#00F0FF] break-all">{result.signature_public}</span>} />
                <Field label="Cardholder" value={result.cardholder_name} accent="text-[#00F0FF]" />
                <Field label="Network" value={result.network} />
                <Field label="Source" value={result.source} />
                <Field label="Reality" value={result.reality} accent={result.reality === "Unreal" ? "text-[#7A5CFF]" : "text-[#00FF9D]"} />
                <Field label="Algorithm" value={result.algorithm} />
              </div>

              {authentic && (
                <button
                  data-testid="verify-certificate-button"
                  onClick={() => {
                    const c = document.getElementById("cert-qr-canvas");
                    const qr = c && c.toDataURL ? c.toDataURL("image/png") : "";
                    printCertificate(result, pubKey, qr);
                  }}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00FF9D] text-black font-semibold h-12 hover:bg-[#00FF9D]/90 transition-colors"
                >
                  <FileText className="w-4 h-4" /> Certificate of Authenticity — print / save PDF
                </button>
              )}
              <div style={{ position: "absolute", left: "-9999px", top: 0 }} aria-hidden="true">
                <QRCodeCanvas
                  id="cert-qr-canvas"
                  value={`${window.location.origin}/verify?id=${encodeURIComponent(result.card_number || result.card_id || "")}`}
                  size={240}
                  level="M"
                  includeMargin
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {pubKey && (
          <div data-testid="verify-public-key" className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3">
            <KeyRound className="w-4 h-4 text-[#00FF9D] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#52525B] mb-1">
                Network public key · {pubKey.algorithm}
              </p>
              <p className="font-mono-sig text-xs text-[#00F0FF] break-all">{pubKey.public_key}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
